/**
 * @file legal-certificates.ts
 * @description Состав автоматической группы «Сертификаты и декларации» в
 * карточке документа (phase19.16, SB141; uiux-legal-steps-b-c §6.4).
 *
 * API отдаёт у группы `PRODUCER_CERTIFICATES` поля `certificates[]` (все
 * сертификаты магазина: производитель → вид → номер, статус на сайте) и
 * `certificateStats { onSite, hidden, expired }`. В shared этих полей нет —
 * типы локальные, чтение терпимое: чего нет или что не того вида —
 * пропускается, неизвестный тон — нейтральный. Группировка по производителю
 * сохраняет порядок сервера.
 */

import type { StatusTone } from "../shop/status-meta";

/** Сертификат в составе группы (как отдаёт API). */
export interface LegalGroupCertificate {
  id: string;
  producerId: string;
  producerName: string;
  typeLabel: string;
  number: string;
  validUntil: string | null;
  siteStatus: { code: string; label: string; tone: StatusTone };
  file: { name: string; sizeBytes: number } | null;
}

export interface LegalCertificateStats {
  onSite: number;
  hidden: number;
  expired: number;
}

/** Производитель и его сертификаты — карточка блока. */
export interface LegalCertificateProducer {
  producerId: string;
  producerName: string;
  certificates: LegalGroupCertificate[];
}

/** Тон сервера → тон пилюли кабинета (`warning` у сервера = «внимание»). */
const TONE: Readonly<Record<string, StatusTone>> = {
  success: "success",
  warning: "attention",
  attention: "attention",
  error: "error",
  neutral: "neutral",
};

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/** Один сертификат или null, если без id. */
function readCertificate(raw: unknown): LegalGroupCertificate | null {
  if (!isObj(raw) || typeof raw.id !== "string") return null;
  const status = isObj(raw.siteStatus) ? raw.siteStatus : {};
  const file = isObj(raw.file) && typeof raw.file.name === "string" ? { name: raw.file.name, sizeBytes: num(raw.file.sizeBytes) } : null;
  return {
    id: raw.id,
    producerId: str(raw.producerId),
    producerName: str(raw.producerName) || "Без производителя",
    typeLabel: str(raw.typeLabel) || str(raw.type) || "Документ",
    number: str(raw.number),
    validUntil: typeof raw.validUntil === "string" ? raw.validUntil : null,
    siteStatus: { code: str(status.code), label: str(status.label) || "—", tone: TONE[str(status.tone)] ?? "neutral" },
    file,
  };
}

/** Сертификаты группы из карточки; поля нет — null (старый API или не та группа). */
export function readGroupCertificates(doc: object): LegalGroupCertificate[] | null {
  const raw = (doc as { certificates?: unknown }).certificates;
  if (!Array.isArray(raw)) return null;
  return raw.map(readCertificate).filter((c): c is LegalGroupCertificate => c !== null);
}

/** Счётчики; нет поля — считаем по сертификатам (на сайте — код `on_site`/`expiring`, просрочен — `expired`). */
export function readCertificateStats(doc: object, certificates: readonly LegalGroupCertificate[] | null): LegalCertificateStats | null {
  const raw = (doc as { certificateStats?: unknown }).certificateStats;
  if (isObj(raw)) return { onSite: num(raw.onSite), hidden: num(raw.hidden), expired: num(raw.expired) };
  if (!certificates) return null;
  const onSite = certificates.filter((c) => c.siteStatus.code === "on_site" || c.siteStatus.code === "expiring").length;
  const expired = certificates.filter((c) => c.siteStatus.code === "expired").length;
  // `hidden` — скрытые не по сроку (как у API); просроченные — отдельно в `expired`.
  return { onSite, hidden: certificates.length - onSite - expired, expired };
}

/** Сертификаты по производителям, порядок сервера сохраняется. */
export function groupCertificatesByProducer(certificates: readonly LegalGroupCertificate[]): LegalCertificateProducer[] {
  const out: LegalCertificateProducer[] = [];
  const index = new Map<string, LegalCertificateProducer>();
  for (const cert of certificates) {
    const key = cert.producerId || cert.producerName;
    let group = index.get(key);
    if (!group) {
      group = { producerId: cert.producerId, producerName: cert.producerName, certificates: [] };
      index.set(key, group);
      out.push(group);
    }
    group.certificates.push(cert);
  }
  return out;
}

/** «1 просрочен», «3 просрочено». */
export function expiredCountText(count: number): string {
  return `${count} ${count % 10 === 1 && count % 100 !== 11 ? "просрочен" : "просрочено"}`;
}

/** Сколько документов группы видно на сайте — для пилюли карточки (у автогруппы — сертификаты на сайте). */
export function legalGroupVisibleCount(doc: { groupSource: string | null; members: readonly { visibleOnSite: boolean }[] | null }): number | undefined {
  if (doc.groupSource === "PRODUCER_CERTIFICATES") return readCertificateStats(doc, readGroupCertificates(doc))?.onSite;
  return doc.members?.filter((m) => m.visibleOnSite).length;
}

/** «На сайте: 5 · не показываются: 3, из них просрочен: 1» — скрытые и просроченные вместе. */
export function certificateStatsText(stats: LegalCertificateStats): string {
  const notShown = stats.hidden + stats.expired;
  const expired = stats.expired > 0 ? `, из них просрочен: ${stats.expired}` : "";
  return `На сайте: ${stats.onSite} · не показываются: ${notShown}${expired}`;
}
